import { runtimeConfig } from '@/lib/config';

import { getAuthTransport } from '@/lib/api/authTransport';
import { clearDelegationToken, delegationToken } from '@/lib/auth/delegationToken';

/**
 * agent(:8000) SSE 대화 스트림.
 *
 * `apiFetch` 를 쓰지 않는다 — 그쪽은 `{ok,data}` JSON 봉투를 한 번에 읽어 파싱하는 전제인데,
 * 여기는 응답이 끝나기 전부터 조금씩 읽어야 하는 `text/event-stream` 이다.
 *
 * 신원은 **위임 토큰**이 나른다({@link delegationToken}) — 에이전트는 세션 쿠키를 모른다.
 * CSRF 헤더는 `AuthTransport` 에서 그대로 가져와 붙인다(에이전트는 무시하지만, 프록시·리버스
 * 프록시 구성이 바뀌어도 요청 모양이 다른 화면들과 어긋나지 않게 둔다).
 *
 * 경로가 `/v1/ai/chat` 인 이유: `/api/v1/ai*` 만 agent 서비스로 프록시된다(vite.config.ts).
 * 대화 **저장**은 여전히 Spring 백엔드의 몫이다(`sessions.ts`) — 저장은 백엔드, 추론은 에이전트.
 *
 * 이벤트 종류는 docs/18 §14.2 에 정의돼 있다. 되물음(`ask`)과 미리보기(`preview`)를 본문 텍스트에
 * 섞지 않고 따로 받는 이유도 거기 있다 — 화면이 문자열을 파싱하게 만들면 모델이 형식을 어기는
 * 순간 화면이 깨진다.
 */

export type AgentEvent =
  /** 진행 상황 표시용. "재직 정보 조회 중" 같은 한 줄. */
  | { type: 'step'; phase: string; label: string }
  /**
   * 답변 조각. 이어 붙이면 본문이 된다.
   *
   * `replace` 가 참이면 <b>이어 붙이지 않고 갈아 끼운다</b> — 에이전트가 흘리던 글자와 완성문이
   * 어긋났을 때(끊긴 스트림·새어 나온 think 태그) 완성문으로 바로잡는 마지막 한 조각이다
   * (`agent/app/llm/stream.py`).
   */
  | { type: 'token'; text: string; replace?: boolean }
  /** 슬롯 되물음. options 가 있으면 버튼으로 그린다. */
  | { type: 'ask'; slot: string; label: string; options?: string[] }
  /** 발급 미리보기 카드 (§10.2). */
  | { type: 'preview'; kind: string; form: string; fields: Record<string, unknown>; watermark: boolean }
  /** 근거 문서 인용 (P2 doc_qa). */
  | { type: 'citation'; documentFileId: string; name: string; clauseRef?: string }
  /** 쓰기 초안. **확인은 사람이 한다** — 프론트가 backend 를 직접 호출한다(§5.4). */
  | { type: 'action'; kind: string; payload: Record<string, unknown> }
  /**
   * 조회 결과를 **그림으로** (DOC-23 §11). `action` 과 달리 누를 것이 없다 —
   * 같은 값이 답변 문장에도 있고, 이건 그것을 달력·표로 옮긴 것뿐이다.
   * 어떤 kind 가 오는지는 `views.ts` 카탈로그가 안다.
   */
  | { type: 'view'; kind: string; payload: Record<string, unknown> }
  /**
   * **눌러서 조회해 보는 칸** (DOC-109 §4). 「그거 API 로는 어떻게 받아?」 하고 물었을 때만 나온다.
   *
   * `view` 와 다른 점은 **누른다**는 것이고, `action` 과 다른 점은 **확인을 묻지 않는다**는 것이다
   * (조회라 되돌릴 것이 없다). 실리는 것은 `key` 와 초기값뿐이다 — **경로가 아니다.** 경로를
   * 실으면 대화에 섞여 들어온 문장 하나가 임의 호출이 된다. 키가 어느 경로인지는 서버의
   * `ProbeCatalog` 만 알고, 화면은 `GET /api/v1/probes` 로 받아 본다.
   */
  | { type: 'probe'; key: string; values?: Record<string, string> }
  /**
   * 대화로 해결하지 못하는 일을 화면으로 넘긴다. 막다른 답변("준비 중입니다")을 만들지 않기 위한 것.
   *
   * `guide` 면 그 화면으로 옮긴 뒤 **그 화면의 사용 안내(헤더의 ?)까지 펼친다** — 사용법을 물은
   * 사람을 빈 화면에 내려놓지 않는다(DOC-20 §1.18). 옮기기만 할 때는 없거나 false 다.
   */
  | { type: 'link'; label: string; href: string; guide?: boolean }
  /**
   * <b>화면의 그 자리를 짚는다</b> — 반짝이는 테두리 (사용자 지시 2026-09-23).
   *
   * <p>「자료 입력 어디서 해?」·「여기 표시해줘」에 <b>말로 설명하지 않는다.</b> 화면이 눈앞에
   * 있는데 「오른쪽 위 파란 버튼」이라고 적는 것은, 보고 있는 사람에게 그림을 글로 옮겨 주는 일이다.
   *
   * <p>{@code target} 은 이번 발화에 <b>우리가 보낸 목록</b>({@code anchors})의 이름이다. 목록에
   * 없는 이름은 화면이 버린다({@code isKnownAnchor}) — `link` 이벤트의 경로 검증과 같은 자리다.
   */
  | { type: 'highlight'; target: string; label?: string }
  /**
   * <b>업무 묶음의 단계표 전부</b> (DOC-133 §3.2). 묶음 실행(`/v1/ai/routines/run`)에서만 온다 —
   * 화면은 이것 하나로 처음부터 끝까지를 세우고, 아래 `plan_step` 으로 줄의 표시만 바꾼다.
   */
  | { type: 'plan'; key: string; title: string; steps: PlanStepSpec[] }
  /** 단계 하나의 상태. `note` 는 끝난 단계의 내용(도구의 답)이거나 멈춘 사유다. */
  | { type: 'plan_step'; id: string; status: PlanStepStatus; note?: string }
  | { type: 'done'; traceId?: string }
  | { type: 'error'; message: string; traceId?: string };

export type PlanStepSpec = { id: string; label: string; kind: string; href?: string | null };
export type PlanStepStatus = 'running' | 'done' | 'failed';

export type AgentChatRequest = {
  message: string;
  /** backend 세션 id. agent 는 이것을 체크포인터 thread_id 로 쓴다(§12). */
  sessionId?: string | null;
  history?: { role: 'user' | 'assistant'; content: string }[];
  /**
   * 지금 보고 있는 화면의 **경로**. 도크에서 물을 때 채워진다 —
   * 「이 페이지 뭐야」가 성립하려면 그 화면을 알아야 한다.
   *
   * **이름표를 보내지 않는다.** 화면 이름은 에이전트의 레지스트리가 이미 들고 있고
   * (`agents/screens.py`, `test_screens_sync.py` 가 이 앱과 대조한다), 이름까지 보내면 두 벌이
   * 되어 어긋나는 날 **없는 화면 이름이 사용자에게 나간다.**
   */
  screen?: string | null;
  /**
   * 그 화면이 지금 열고 있는 **현장 이름**(`useScreenSite`). 「이 현장 원가율 어때」 가
   * 성립하려면 필요하다.
   *
   * **id 가 아니라 이름이다.** 에이전트의 도구는 이름을 받아 스스로 id 로 바꾸고
   * (`tools/_site_ref.py`), 못 찾으면 후보를 되묻는다 — 모델도 화면도 UUID 를 다루지 않는다.
   */
  site?: string | null;
  /**
   * 이번 발화에 <b>끌어다 붙인 자료실 파일</b>(`docDrag.ts` 의 규약 그대로).
   *
   * <p><b>이름이 아니라 키를 보낸다.</b> 말풍선에는 「[참조 문서] 이름」 한 줄이 남지만, 그 이름은
   * 가리키는 말로 쓰기에 너무 애매하다 — 회사 파일 이름이 부서·사람·숫자를 그대로 담고 있어
   * (`20260903_공무팀_…png`) 모델이 「공무팀」을 <b>현장 이름</b>으로 읽고 「그런 현장이 없습니다」로
   * 답한 적이 있다(실측 2026-09-04).
   *
   * <p>키가 있으면 에이전트는 그 파일이 <b>이미 분석돼 있는지</b>를 묻고, 아니면 그 자리에서
   * 분석한 뒤 답을 잇는다. 이 값은 그 요청 한 번에만 실린다 — 다음 턴으로 따라가지 않는다.
   */
  docs?: { source: 'S3' | 'NAS'; ref: string; name: string; connectionId?: string | null }[];
  /**
   * 그 화면에서 지금 열려 있는 <b>탭의 권한 키</b>(`useScreenTab`). 「이거 수정하는 건 누구야」가
   * 성립하려면 필요하다 — 탭은 주소의 `?tab=` 에 있어 위 {@code screen}(경로)에 실리지 않는다.
   *
   * <p><b>이름이 아니라 키다.</b> 탭 이름은 이 앱이 소유하고 에이전트에는 목록이 없다. 키를 주면
   * 이름을 몰라도 「누가 보고 누가 고치나」를 서버에 물을 수 있고, 이름은 그쪽 표가 옮긴다.
   */
  tab?: {
    menuKey: string;
    label: string;
    slot?: { label: string; categoryCode: string };
  } | null;
  /**
   * 그 화면에 <b>지금 떠 있는 자리들</b>(`lib/tour/anchors`). 「어디서 해?」 에 말 대신 <b>짚어 줄</b>
   * 수 있게 하는 목록이다 — 에이전트는 여기서 고르고, 고른 이름이 {@code highlight} 로 돌아온다.
   *
   * <p><b>화면이 보낸다.</b> 이 목록은 그 사람의 권한·그 탭·그 순간에만 참이라 에이전트가 표로
   * 들 수 없다(`anchors.ts` 머리말). 화면 경로를 이름표 없이 보내는 것과 반대 방향의 규율이다.
   */
  anchors?: { name: string; label: string }[];
};

/**
 * 같은 대화의 **전부**를 실어 보낸다.
 *
 * 한때 최근 10턴만 보냈는데, 그러면 "아까 말한 그거" 가 열한 번째 턴에서 통하지 않는다.
 * 같은 세션 안에서는 나눈 말을 다 기억하는 것이 맞다 — 자르는 판단은 화면이 할 일이 아니다.
 *
 * 컨텍스트 한계는 에이전트가 다룬다(`app/api/routes/chat.py` 의 길이 경고). 여기서 미리 잘라
 * 보내면 잘렸다는 사실조차 아무 데도 남지 않는다.
 */
export function toHistory(messages: { role: 'user' | 'assistant'; content: string }[]) {
  return messages.map(({ role, content }) => ({ role, content }));
}

/**
 * 스트림을 열고 이벤트를 하나씩 `onEvent` 로 넘긴다. 스트림이 끝나면 resolve 한다.
 *
 * 네트워크 오류는 `error` 이벤트로 바꿔 넘긴다 — 호출부가 예외와 이벤트 두 갈래를 따로
 * 다루지 않게 하려는 것이다.
 */
export async function streamAgentChat(
  request: AgentChatRequest,
  onEvent: (event: AgentEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return streamAgentSse('/v1/ai/chat', {
    message: request.message,
    sessionId: request.sessionId ?? null,
    history: request.history ?? [],
    screen: request.screen ?? null,
    site: request.site ?? null,
    // 필드를 <b>여기 적어야</b> 실려 간다 — 이 자리는 `request` 를 통째로 넘기지 않고
    // 하나씩 옮긴다(보낼 것을 눈으로 셀 수 있게). 타입에만 더하면 조용히 안 간다.
    docs: request.docs ?? [],
    tab: request.tab ?? null,
    anchors: request.anchors ?? [],
  }, onEvent, signal);
}

/**
 * 업무 묶음 한 바퀴 (DOC-133 · AI Agent 모드). 대화와 같은 입구·같은 이벤트 모양이고,
 * `plan`·`plan_step` 이 더해질 뿐이다.
 */
export function streamRoutine(
  request: { recipe: string; params?: Record<string, unknown>; sessionId?: string | null },
  onEvent: (event: AgentEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return streamAgentSse('/v1/ai/routines/run', {
    recipe: request.recipe,
    params: request.params ?? {},
    sessionId: request.sessionId ?? null,
  }, onEvent, signal);
}

async function streamAgentSse(
  path: string,
  body: Record<string, unknown>,
  onEvent: (event: AgentEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const transport = getAuthTransport();

  // 에이전트는 세션 쿠키를 모른다. 백엔드에서 받은 단기 위임 토큰이 신원을 나른다(docs/18 §5.1).
  let token: string;
  try {
    token = await delegationToken();
  } catch {
    clearDelegationToken();
    onEvent({ type: 'error', message: 'AI 사용 권한을 확인하지 못했습니다. 다시 로그인해 주세요.' });
    return;
  }

  let response: Response;
  try {
    response = await fetch(`${runtimeConfig().apiBaseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
        ...transport.authHeaders({ unsafe: true }),
      },
      body: JSON.stringify(body),
      credentials: transport.credentials,
      signal,
    });
  } catch {
    onEvent({ type: 'error', message: 'AI 에이전트에 연결하지 못했습니다.' });
    return;
  }

  if (!response.ok || !response.body) {
    // 토큰이 문제였을 수 있으니 캐시를 버린다. 다음 시도는 새 토큰으로 나간다.
    if (response.status === 401) clearDelegationToken();

    // 에이전트가 준 사유를 그대로 보여 준다. "다시 로그인하세요" 같은 짐작을 앞세우면, 실제 원인이
    // 위임 토큰 미발급이나 서버 설정일 때 사용자가 엉뚱한 곳을 헤맨다.
    const detail = await response
      .clone()
      .json()
      .then((body: { detail?: string }) => body?.detail)
      .catch(() => undefined);

    onEvent({
      type: 'error',
      message: detail ?? `AI 에이전트가 응답하지 않습니다. (HTTP ${response.status})`,
    });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // SSE 는 빈 줄로 이벤트를 가른다. 청크 경계가 이벤트 중간에 떨어질 수 있어 버퍼에 모아 자른다.
  //
  // ★ 읽는 동안의 오류를 여기서 잡는다. 밖으로 던지면 호출부의 catch 하나에 뭉쳐서
  //   "대화를 저장하지 못했습니다" 로 표시됐다 — 저장은 멀쩡한데 저장 탓을 하는 셈이었고,
  //   받다 만 답변까지 함께 사라졌다. 스트림이 끊기는 것은 저장 실패와 다른 사건이다.
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');

        const payload = block
          .split('\n')
          .filter((line) => line.startsWith('data: '))
          .map((line) => line.slice(6))
          .join('\n');
        if (!payload) continue;

        try {
          onEvent(JSON.parse(payload) as AgentEvent);
        } catch {
          // 깨진 한 조각 때문에 대화 전체를 끊지 않는다.
        }
      }
    }
  } catch {
    // 여기까지 받은 답변은 호출부가 이미 들고 있다. 그것을 살린 채 끊겼다는 사실만 알린다.
    onEvent({ type: 'error', message: '답변을 받는 중 연결이 끊겼습니다.' });
  }
}
